import type { TvvPeriodeFactorenDto } from "@/shared/types/generated/dtos";
import { createInterval } from "@/shared/utils";
import { isSaturday, isSunday, isWithinInterval } from "date-fns";
import { Reisperiode } from "../constants";

export type PeriodeFactor = {
    periode: Reisperiode;
    reizigersPrognose: number;
    deelBussenBeginStation: number;
    afhaakPercentage: number;
};

export type InitieleBestelWaarden = {
    busFrequentie: number;
    aantalBussen: number;
    percentageBeginStation: number;
    bussenBeginStation: number;
    bussenEindStation: number;
};

const BUS_CAPACITEIT = 50;
const BUFFER_TIJD_MINUTEN = 10;

/**
 * Calculates the cycle time in minutes
 * Formula: omlooptijd = 2 * rijtijd + buffertijd
 */
function berekenOmlooptijd(rijtijdMinuten: number): number {
    return 2 * rijtijdMinuten + BUFFER_TIJD_MINUTEN;
}

/**
 * Calculates the number of buses based on bus frequency and travel time
 * Formula: aantalBussen = omlooptijd / 60 * busfrequentie
 */
export function berekenAantalBussen(busFrequentie: number, rijtijdMinuten: number): number {
    if (busFrequentie <= 0 || rijtijdMinuten <= 0) return 0;
    const omlooptijd = berekenOmlooptijd(rijtijdMinuten);
    return Math.ceil((omlooptijd / 60) * busFrequentie);
}

/**
 * Calculates the bus frequency based on number of buses and travel time
 * Formula: busfrequentie = aantalBussen * 60 / omlooptijd
 */
export function berekenBusFrequentie(aantalBussen: number, rijtijdMinuten: number): number {
    if (aantalBussen <= 0 || rijtijdMinuten <= 0) return 1;
    const omlooptijd = berekenOmlooptijd(rijtijdMinuten);
    const frequentie = (aantalBussen * 60) / omlooptijd;
    return Math.max(1, Math.round(frequentie));
}

/**
 * Calculates the bus distribution based on the percentage of buses at the start station
 * Formula: bussenBeginStation = totaalBussen * (percentageBeginStation / 100)
 */
export function berekenBussenVerdeling(
    totaalBussen: number,
    percentageBeginStation: number
): { bussenBeginStation: number; bussenEindStation: number } {
    const bussenBeginStation = Math.round(totaalBussen * (percentageBeginStation / 100));
    return { bussenBeginStation, bussenEindStation: totaalBussen - bussenBeginStation };
}

/**
 * Calculates the percentage of buses at the start station based on the number of buses at the start station and total buses
 * Formula: percentageBeginStation = (bussenBeginStation / totaalBussen) * 100
 */
export function berekenPercentageBeginStation(bussenBeginStation: number, totaalBussen: number): number {
    if (totaalBussen === 0) return 0;
    return Math.round((bussenBeginStation / totaalBussen) * 100);
}

/**
 * Determines the current travel period based on the current time
 * - Ochtendspits: 06:30 - 09:00
 * - Avondspits: 16:00 - 18:30
 * - Superdal: 22:00 - 06:30
 * - Dal: other times
 * - Zaterdag/Zondag: all day
 */
export function bepaalHuidigeReisperiode(): Reisperiode {
    const now = new Date();
    const hour = now.getHours();
    const minutes = now.getMinutes();
    const dayOfWeek = now.getDay();

    if (dayOfWeek === 0) return Reisperiode.Zondag;
    if (dayOfWeek === 6) return Reisperiode.Zaterdag;

    const timeInMinutes = hour * 60 + minutes;
    if (timeInMinutes >= 390 && timeInMinutes < 540) return Reisperiode.Ochtendspits;
    if (timeInMinutes >= 960 && timeInMinutes < 1110) return Reisperiode.Avondspits;
    if (timeInMinutes >= 1320 || timeInMinutes < 390) return Reisperiode.Superdal;

    return Reisperiode.Dal;
}

/**
 * Calculates the initial bus frequency based on passenger forecast
 * Formula: busfrequentie = nettoprognose / capaciteit
 * where nettoprognose = reizigersprognose * (1 - afhaakpercentage / 100)
 */
export function berekenInitieleBusFrequentie(reizigersPrognose: number, afhaakPercentage: number): number {
    const afhaakfactor = 1 - afhaakPercentage / 100;
    const nettoprognose = reizigersPrognose * afhaakfactor;
    const frequentie = nettoprognose / BUS_CAPACITEIT;
    return Math.max(2, Math.round(frequentie)); // minimum 2
}

export const PeriodeFactor = {
    fromDto(dto: TvvPeriodeFactorenDto): PeriodeFactor {
        return {
            ...dto,
            periode: dto.periode,
        };
    },

    getPeriodeFactor(periodeFactoren: PeriodeFactor[], periode: Reisperiode): PeriodeFactor | undefined {
        return periodeFactoren.find((pf) => pf.periode === periode);
    },

    getInitieleBestelWaarden(factor: PeriodeFactor, rijtijdMinuten: number): InitieleBestelWaarden {
        const busFrequentie = berekenInitieleBusFrequentie(factor.reizigersPrognose, factor.afhaakPercentage);
        const aantalBussen = berekenAantalBussen(busFrequentie, rijtijdMinuten);
        const percentageBeginStation = Math.round(factor.deelBussenBeginStation * 100);

        return {
            busFrequentie,
            aantalBussen,
            percentageBeginStation,
            ...berekenBussenVerdeling(aantalBussen, percentageBeginStation),
        };
    },
    getCurrentReisperiode(periodeFactoren: PeriodeFactor[]): Reisperiode {
        if (periodeFactoren.length === 0) {
            return Reisperiode.Handmatig;
        }

        const now = new Date();
        const hasPeriodeFactor = (reisPeriode: Reisperiode) => periodeFactoren.find((p) => p.periode === reisPeriode);

        // check weekend
        if (isSaturday(now) && hasPeriodeFactor("Zaterdag")) {
            return Reisperiode.Zaterdag;
        } else if (isSunday(now) && hasPeriodeFactor("Zondag")) {
            return Reisperiode.Zondag;
        }

        // then it's a week day, so check ochtend- and avondspits
        const avondSpits = createInterval("15:30", "18:00");
        const ochtendSpits = createInterval("05:30", "09:00");
        if (isWithinInterval(now, avondSpits) && hasPeriodeFactor("Avondspits")) {
            return Reisperiode.Avondspits;
        } else if (isWithinInterval(now, ochtendSpits) && hasPeriodeFactor("Ochtendspits")) {
            return Reisperiode.Ochtendspits;
        } else {
            return Reisperiode.Dal;
        }
    },
};
